/**
 * RideFinishedSheet — customer "You've arrived" slide-up at drop-off: fare, tip
 * picker and one-tap rating, then straight into the pay sheet (tip carried over).
 * Laundry / trash pay earlier and keep their own flow, so they are skipped here.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Flag, Star, Wallet, Loader2 } from "lucide-react";
import { API, useAuth } from "@/App";
import PersistentSheet from "@/components/requests/PersistentSheet";
import { SERVICE_TONES, SERVICE_LABELS, serviceToneKey } from "@/lib/serviceTones";
import { useCustomerTripEvents, seenSet, markSeen } from "@/lib/useCustomerTripEvents";
import { proNoun } from "@/components/CustomerAcceptedSheet";
import { tripFare } from "@/utils/tripFare";
import { playSoftChime } from "@/lib/chime";

const SEEN_KEY = "yorm:cust-finished-seen";
const TYPES = ["rideshare_completed", "rideshare_delivered", "rideshare_completed_runner"];
const TIPS = [0, 2, 5, 10];
const fmt = (n) => `GHS ${Number(n || 0).toFixed(2)}`;

const headline = (b) => {
  const t = (b?.rideshare_type || "").toLowerCase();
  if (t === "delivery") return "Package delivered";
  if (t === "runner") return "Errand done";
  if (t === "van") return "Trip complete";
  return "You've arrived";
};

export default function RideFinishedSheet({ demo }) {
  const { token } = useAuth() || {};
  const navigate = useNavigate();
  const [trip, setTrip] = useState(demo || null);
  const [tip, setTip] = useState(demo?.demo_tip || 0);
  const [custom, setCustom] = useState("");
  const [rating, setRating] = useState(demo?.demo_rating || 0);
  const [hover, setHover] = useState(0);
  const [rated, setRated] = useState(false);
  const [busy, setBusy] = useState(false);

  useCustomerTripEvents(TYPES, (b) => {
    if (b.status !== "completed" || ["laundry", "trash"].includes(b.service_flavor) || seenSet(SEEN_KEY).has(b.id)) return;
    markSeen(SEEN_KEY, b.id);
    playSoftChime();
    setTip(0); setCustom(""); setRating(Number(b.rating || 0)); setRated(!!b.rating);
    setTrip(b);
  });

  // Hold the global "Time to pay" prompt back while this sheet is up (it takes over on Pay / Later).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("yorm:pay-prompts-pause", { detail: { paused: !!trip } }));
    return () => window.dispatchEvent(new CustomEvent("yorm:pay-prompts-pause", { detail: { paused: false } }));
  }, [trip]);

  if (!trip) return null;
  const tone = SERVICE_TONES[serviceToneKey(trip)];
  const label = SERVICE_LABELS[serviceToneKey(trip)] || "Trip";
  const noun = proNoun(trip);
  const name = trip.artisan_name || `your ${noun}`;
  const fare = tripFare(trip) ?? Number(trip.final_fare || trip.agreed_fare || 0);
  const paid = trip.payment_status === "paid" || trip.pro_paid_status === "paid" || !!trip.pro_paid_at;
  const tipValue = custom !== "" ? Math.max(0, Number(custom) || 0) : tip;
  const total = fare + tipValue;

  const rate = async (v) => {
    setRating(v);
    try {
      await axios.post(`${API}/rideshare/bookings/${trip.id}/rate?token=${token}`, { rating: v, comment: null });
      setRated(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your rating");
    }
  };
  const pay = () => {
    setBusy(true);
    const id = trip.id;
    const detail = {
      kind: "booking", id, rideshare: true, amount: fare, tip: tipValue, label,
      pro_name: trip.artisan_name || `your ${noun}`, pro_role: trip.pro_role || noun,
      cash: trip.customer_payment_choice === "cash", live: false,
    };
    setTrip(null); setBusy(false);
    window.dispatchEvent(new CustomEvent("yorm:open-payment", { detail }));
  };
  const later = () => { const id = trip.id; setTrip(null); if (!window.location.pathname.includes(id)) navigate(`/customer/trip/${id}`); };

  return (
    <PersistentSheet open title={headline(trip)} icon={Flag} testId="ride-finished-sheet" className="pay-glass">
      <div className="space-y-4" style={{ "--svc": tone.main, "--svc-dark": tone.dark }}>
        <div className="flex flex-col items-center text-center pt-1">
          <span className="h-14 w-14 rounded-full flex items-center justify-center text-white shadow-md animate-in zoom-in-50 duration-300" style={{ background: tone.main }}>
            <Flag size={26} strokeWidth={2.5} />
          </span>
          <p className="mt-3 text-[17px] font-semibold text-[#222]" data-testid="ride-finished-headline">{headline(trip)}</p>
          <p className="text-[13px] text-gray-500" data-testid="ride-finished-sub">
            {label} with {name}{trip.dropoff_address ? ` · ${trip.dropoff_address}` : ""}
          </p>
        </div>

        <div className="rounded-2xl bg-white/80 p-4 space-y-3" data-testid="ride-finished-fare-card">
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-gray-500">Fare</span>
            <span className="text-[22px] font-bold text-[#222] tabular-nums" data-testid="ride-finished-fare">{fmt(fare)}</span>
          </div>
          {!paid && (
            <>
              <p className="text-[12px] uppercase tracking-wide text-gray-500 pt-2 border-t border-gray-100">Add a tip for {name.split(" ")[0]}</p>
              <div className="grid grid-cols-5 gap-2" data-testid="ride-finished-tip-picker">
                {TIPS.map((v) => (
                  <button key={v} type="button" onClick={() => { setTip(v); setCustom(""); }} data-testid={`ride-finished-tip-${v}`}
                    className={`h-10 rounded-xl text-[14px] font-semibold border transition-colors ${custom === "" && tip === v ? "bg-[var(--svc)] text-white border-transparent" : "bg-white text-[#222] border-gray-200"}`}>
                    {v === 0 ? "None" : `+${v}`}
                  </button>
                ))}
                <input type="number" inputMode="decimal" min="0" placeholder="Other" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="ride-finished-tip-custom"
                  className={`h-10 rounded-xl text-[14px] font-semibold border text-center ${custom !== "" ? "border-[var(--svc)] text-[var(--svc-dark)]" : "border-gray-200 text-[#222]"}`} />
              </div>
            </>
          )}
          <div className="pt-2 border-t border-gray-100">
            <p className="text-[12px] uppercase tracking-wide text-gray-500 mb-1">{rated ? "Thanks for rating" : `Rate your ${noun}`}</p>
            <div className="flex items-center justify-center gap-2" data-testid="ride-finished-rating" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((v) => (
                <button key={v} type="button" onClick={() => rate(v)} onMouseEnter={() => setHover(v)} data-testid={`ride-finished-star-${v}`} className="p-1 transition-transform active:scale-110" aria-label={`${v} star${v === 1 ? "" : "s"}`}>
                  <Star size={30} className={(hover || rating) >= v ? "fill-[#F2C14E] text-[#F2C14E]" : "text-gray-300"} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {paid ? (
          <Button onClick={later} className="w-full h-12 rounded-xl text-white text-[16px] font-semibold bg-[var(--svc)] hover:bg-[var(--svc-dark)]" data-testid="ride-finished-done-btn">Done</Button>
        ) : (
          <Button onClick={pay} disabled={busy} className="w-full h-12 rounded-xl text-white text-[16px] font-semibold bg-[var(--svc)] hover:bg-[var(--svc-dark)]" data-testid="ride-finished-pay-btn">
            {busy ? <Loader2 size={18} className="animate-spin mr-2" /> : <Wallet size={18} className="mr-2" />} Pay {fmt(total)}{tipValue > 0 ? " incl. tip" : ""}
          </Button>
        )}
        {!paid && (
          <button type="button" onClick={later} className="w-full text-[15px] font-medium text-gray-500 pb-1" data-testid="ride-finished-later-btn">Pay later from the trip</button>
        )}
      </div>
    </PersistentSheet>
  );
}
